<template>
  <div class="contact-form" data-aos="fade">
    <Transition name="fade" mode="out-in">
      <form class="contact-form__form" v-if="!success">
        <TextInput
          class="contact-form__input"
          v-model="model.firstName"
          placeholder="First Name"
          required
          :error="errors.firstName"
          icon="fa-user"
        />
        <TextInput
          class="contact-form__input"
          v-model="model.lastName"
          placeholder="Last Name"
          required
          :error="errors.lastName"
          icon="fa-user"
        />
        <TextInput
          class="contact-form__input contact-form__input_email"
          v-model="model.email"
          placeholder="E-Mail"
          required
          :error="errors.email"
          icon="fa-envelope"
        />
        <TextInput
          class="contact-form__input contact-form__input_message"
          v-model="model.message"
          placeholder="Message"
          required
          :error="errors.message"
          type="textarea"
        />
        <div class="contact-form__footer">
          <Button class="contact-form__submit-button" @click.prevent="submit">
            Send
          </Button>
        </div>
      </form>
      <div class="contact-form__submit-feedback" v-else>
        <p class="contact-form__feedback-title">Thank you!</p>
        <p class="contact-form__feedback-text">
          Thanks a lot for getting in touch with us! We've received your
          message.
        </p>
        <Button
          class="contact-form__feedback-button"
          @click.prevent="resetForm"
          size="sm"
        >
          New Message
        </Button>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
  import Button from "../buttons/Button.vue";
  import type { PropertiesToType } from "@common/types/utility";

  const email = "hello@speedway-games.com";

  const { create } = useFeedbackClient();

  const model = reactive<{
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    message: string | null;
  }>({
    firstName: null,
    lastName: null,
    email: null,
    message: null,
  });
  const errors = reactive<Partial<PropertiesToType<typeof model, string>>>({});
  const success = ref<boolean>(false);

  async function submit() {
    if (!model.firstName || !model.lastName || !model.email || !model.message) {
      errors.firstName = model.firstName ? undefined : "Required";
      errors.lastName = model.lastName ? undefined : "Required";
      errors.email = model.email ? undefined : "Required";
      errors.message = model.message ? undefined : "Required";
      success.value = false;
      return;
    }

    const result = await create({
      name: model.firstName + " " + model.lastName,
      email: model.email,
      message: model.message,
    });

    errors.firstName = result?.validationErrors.name?.[0];
    errors.lastName = result?.validationErrors.name?.[0];
    errors.email = result?.validationErrors.email?.[0];
    errors.message = result?.validationErrors.message?.[0];

    if (result?.validationErrors) {
      success.value = false;
    } else {
      success.value = true;
    }
  }

  async function resetForm() {
    success.value = false;
    model.firstName = null;
    model.lastName = null;
    model.message = null;
    model.email = null;
    errors.firstName = undefined;
    errors.lastName = undefined;
    errors.message = undefined;
    errors.email = undefined;
  }
</script>

<style lang="scss">
  .contact-form {
    &__form {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px;

      width: 100%;

      @media (max-width: $md) {
        grid-template-columns: 1fr;
      }
    }

    &__input {
      &_email,
      &_message {
        grid-column: span 2;
        @media (max-width: $md) {
          grid-column: auto;
        }
      }
    }

    &__submit-button {
      width: fit-content;
    }

    &__submit-feedback {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 8px;

      text-align: center;

      min-height: 200px;
    }

    &__feedback-title {
      color: #212529;
      font-family: var(--font-family-main);
      font-size: 40px;
      font-weight: 700;
      line-height: 1;
      text-transform: capitalize;

      @media (max-width: $md) {
        font-size: 32px;
      }
    }

    &__feedback-text {
      color: #212529;
      font-family: var(--font-family-main);
      font-size: 16px;
      font-weight: 400;

      max-width: 400px;
    }

    &__footer {
      display: flex;
      gap: 16px;
      align-items: center;
      justify-content: space-between;

      grid-column: 1 / -1;
    }

    &__alternative-email {
      color: #212529;
      font-family: var(--font-family-main);
      text-align: right;
      font-size: 16px;
      font-weight: 600;

      max-width: 400px;

      a {
        color: inherit;
      }
    }
  }
</style>
